import { BrowserRouter, Route, Routes } from "react-router-dom";
import { SessionIdleGuard } from "@/components/session-idle-guard";
import { AppProviders } from "@/providers/app-providers";
import { DesignSystemPage } from "@/pages/design-system";
import { HomePage } from "@/pages/home";
import { PostLoginPage } from "@/pages/auth/post-login";
import { SignupPage } from "@/pages/signup";
import { TenantLayout } from "@/layouts/tenant-layout";
import { TenantLoginPage } from "@/pages/tenant/login";
import { TenantRegisterPage } from "@/pages/tenant/register";
import { TenantForgotPasswordPage } from "@/pages/tenant/forgot-password";
import { TenantResetPasswordPage } from "@/pages/tenant/reset-password";
import { TenantHomePage } from "@/pages/tenant/home";
import { TenantAboutPage } from "@/pages/tenant/about";
import { TenantCatalogPage } from "@/pages/tenant/catalog";
import { TenantCourseDetailPage } from "@/pages/tenant/course-detail";
import { TenantDashboardPage } from "@/pages/tenant/dashboard";
import { TenantLearnIndexPage } from "@/pages/tenant/learn/index";
import { TenantLearnLessonPage } from "@/pages/tenant/learn/lesson";
import { TenantLearnCommunityPage } from "@/pages/tenant/learn/community";
import { TenantLearnAnnouncementsPage } from "@/pages/tenant/learn/announcements";
import {
  TenantCheckoutSuccessPage,
  TenantMockCheckoutPage,
  TenantMockSubscriptionCheckoutPage,
} from "@/pages/tenant/checkout";
import { TenantInstructorDashboardPage } from "@/pages/tenant/instructor/index";
import { TenantInstructorCoursesPage } from "@/pages/tenant/instructor/courses/index";
import { TenantInstructorCourseEditorPage } from "@/pages/tenant/instructor/courses/course-editor";
import { TenantInstructorCourseGradebookPage } from "@/pages/tenant/instructor/courses/gradebook";
import { TenantInstructorCourseAnalyticsPage } from "@/pages/tenant/instructor/courses/analytics";
import { TenantInstructorStudentDetailPage } from "@/pages/tenant/instructor/courses/student-detail";
import { TenantAdminMembersPage } from "@/pages/tenant/admin/members";
import { TenantAdminBrandingPage } from "@/pages/tenant/admin/branding";
import { TenantAdminEmailPage } from "@/pages/tenant/admin/email";
import { TenantAdminApprovedEmailsPage } from "@/pages/tenant/admin/approved-emails";
import { TenantAdminJoinRequestsPage } from "@/pages/tenant/admin/join-requests";
import { TenantAdminAccessReportPage } from "@/pages/tenant/admin/access-report";
import { TenantPrivacyPage, TenantTermsPage } from "@/pages/tenant/legal";
import { TenantForumPage } from "@/pages/tenant/forum";
import { TenantMessagesPage } from "@/pages/tenant/messages";
import { TenantNotificationsPage } from "@/pages/tenant/notifications";
import { TenantPrivacySettingsPage } from "@/pages/tenant/settings/privacy";
import { TenantAccountRestorePage } from "@/pages/tenant/account-restore";
import { TenantInviteRedeemPage } from "@/pages/tenant/invite/redeem";
import { TenantSubscriptionsPage } from "@/pages/tenant/subscriptions";
import { TenantVerifyEmailPage } from "@/pages/tenant/verify-email";
import { TenantMockLiveRoomPage } from "@/pages/tenant/live/mock-room";
import { PlatformAdminPage } from "@/pages/platform/admin";
import { PlatformLoginPage } from "@/pages/platform/login";
import { PlatformSchoolPage } from "@/pages/platform/school";
import { PlatformSetPasswordPage } from "@/pages/platform/set-password";
import { PlatformForgotPasswordPage } from "@/pages/platform/forgot-password";
import { NotFoundPage } from "@/pages/not-found";
import { AppErrorBoundary } from "@/components/app-error-boundary";

export default function App() {
  return (
    <AppProviders>
      {/* OPS-8: a render crash shows a translated page, never a blank screen. */}
      <AppErrorBoundary>
        <BrowserRouter>
          <SessionIdleGuard>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/auth/post-login" element={<PostLoginPage />} />
              <Route path="/signup" element={<SignupPage />} />
              <Route path="/design-system" element={<DesignSystemPage />} />
              {/* MT-1 S23a (MT-1-D506): the picker is gone; "/" until FR-20 "Find your school". */}
              <Route path="/select-tenant" element={<HomePage />} />
              <Route path="/platform/admin" element={<PlatformAdminPage />} />
              <Route path="/platform/admin/schools/:tenantId" element={<PlatformSchoolPage />} />
              <Route path="/platform/login" element={<PlatformLoginPage />} />
              <Route path="/platform/set-password" element={<PlatformSetPasswordPage />} />
              <Route path="/platform/forgot-password" element={<PlatformForgotPasswordPage />} />
              <Route path="/t/:tenantSlug" element={<TenantLayout />}>
                <Route index element={<TenantHomePage />} />
                <Route path="about" element={<TenantAboutPage />} />
                <Route path="courses" element={<TenantCatalogPage />} />
                <Route path="courses/:courseSlug" element={<TenantCourseDetailPage />} />
                <Route path="dashboard" element={<TenantDashboardPage />} />
                <Route path="instructor" element={<TenantInstructorDashboardPage />} />
                <Route path="instructor/courses" element={<TenantInstructorCoursesPage />} />
                <Route
                  path="instructor/courses/:courseId"
                  element={<TenantInstructorCourseEditorPage />}
                />
                <Route
                  path="instructor/courses/:courseId/gradebook"
                  element={<TenantInstructorCourseGradebookPage />}
                />
                <Route
                  path="instructor/courses/:courseId/analytics"
                  element={<TenantInstructorCourseAnalyticsPage />}
                />
                <Route
                  path="instructor/courses/:courseId/students/:userId"
                  element={<TenantInstructorStudentDetailPage />}
                />
                <Route path="admin/members" element={<TenantAdminMembersPage />} />
                <Route path="admin/branding" element={<TenantAdminBrandingPage />} />
                <Route path="admin/email" element={<TenantAdminEmailPage />} />
                <Route path="admin/approved-emails" element={<TenantAdminApprovedEmailsPage />} />
                <Route path="admin/join-requests" element={<TenantAdminJoinRequestsPage />} />
                <Route path="admin/access-report" element={<TenantAdminAccessReportPage />} />
                <Route path="terms" element={<TenantTermsPage />} />
                <Route path="privacy" element={<TenantPrivacyPage />} />
                <Route path="forum" element={<TenantForumPage />} />
                <Route path="messages" element={<TenantMessagesPage />} />
                <Route path="notifications" element={<TenantNotificationsPage />} />
                <Route path="settings/privacy" element={<TenantPrivacySettingsPage />} />
                <Route path="account/restore" element={<TenantAccountRestorePage />} />
                <Route path="invite/:token" element={<TenantInviteRedeemPage />} />
                <Route path="subscriptions" element={<TenantSubscriptionsPage />} />
                <Route path="verify-email" element={<TenantVerifyEmailPage />} />
                <Route path="live/mock/:sessionId" element={<TenantMockLiveRoomPage />} />
                <Route path="learn/:courseSlug" element={<TenantLearnIndexPage />} />
                <Route path="learn/:courseSlug/community" element={<TenantLearnCommunityPage />} />
                <Route
                  path="learn/:courseSlug/announcements"
                  element={<TenantLearnAnnouncementsPage />}
                />
                <Route
                  path="learn/:courseSlug/lessons/:lessonId"
                  element={<TenantLearnLessonPage />}
                />
                <Route path="checkout/success" element={<TenantCheckoutSuccessPage />} />
                <Route path="checkout/mock/:paymentId" element={<TenantMockCheckoutPage />} />
                <Route
                  path="checkout/subscription/mock/:subscriptionPaymentId"
                  element={<TenantMockSubscriptionCheckoutPage />}
                />
                <Route path="login" element={<TenantLoginPage />} />
                <Route path="register" element={<TenantRegisterPage />} />
                <Route path="forgot-password" element={<TenantForgotPasswordPage />} />
                <Route path="reset-password" element={<TenantResetPasswordPage />} />
                {/* OPS-8: unknown tenant URL — keep the tenant chrome, say so. */}
                <Route path="*" element={<NotFoundPage />} />
              </Route>
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </SessionIdleGuard>
        </BrowserRouter>
      </AppErrorBoundary>
    </AppProviders>
  );
}
